import {
    collection,
    query,
    getDocs,
    getDoc,
    addDoc,
    doc,
    updateDoc,
    where,
    orderBy,
    limit,
    increment
} from 'firebase/firestore';

import * as Sentry from '@sentry/react';
import { db } from '../firebase/firebase';
import { Customer } from '../models/Customer';
import userService from './user.service';

class CustomerService {
    constructor() {
        this.collection = 'customers';
        this.cache = new Map(); // phoneDigits -> Customer
        this.cacheLoaded = false;
        this.cacheTimestamp = null;
        this.CACHE_SIZE = 500;
        this.CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes
    }

    async loadRecentCustomers() {
        if (this.cacheLoaded && !this.isCacheExpired()) {
            return;
        }

        try {
            const storeId = await userService.getUserStoreId();
            const collectionRef = collection(db, this.collection);
            const q = query(
                collectionRef,
                where('store_id', '==', storeId),
                orderBy('lastOrderAt', 'desc'),
                limit(this.CACHE_SIZE)
            );

            const querySnapshot = await getDocs(q);
            this.cache.clear();

            querySnapshot.forEach((doc) => {
                const customer = Customer.fromFirebase(doc);
                this.cache.set(customer.phoneDigits, customer);
            });

            this.cacheLoaded = true;
            this.cacheTimestamp = Date.now();
            console.log(`Loaded ${this.cache.size} customers into cache`);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error loading customer cache:', error);
        }
    }

    isCacheExpired() {
        if (!this.cacheTimestamp) return true;
        return Date.now() - this.cacheTimestamp > this.CACHE_EXPIRY_MS;
    }

    addToCache(customer) {
        if (customer && customer.phoneDigits) {
            this.cache.set(customer.phoneDigits, customer);
        }
    }

    searchCache(query) {
        const results = [];
        const normalizedQuery = query.toLowerCase().replace(/\D/g, '');
        const isPhoneSearch = normalizedQuery.length >= 3 &&
            normalizedQuery.length / query.replace(/\s/g, '').length > 0.7;

        for (const customer of this.cache.values()) {
            if (isPhoneSearch) {
                if (customer.phoneDigits.includes(normalizedQuery)) {
                    results.push(customer);
                }
            } else {
                const searchQuery = query.toLowerCase();
                if (customer.searchName.includes(searchQuery)) {
                    results.push(customer);
                }
            }
            if (results.length >= 8) break;
        }

        return results;
    }

    async searchCustomers(searchQuery) {
        if (!searchQuery || searchQuery.length < 2) {
            return [];
        }

        await this.loadRecentCustomers();

        // Search cache first
        let results = this.searchCache(searchQuery);

        // If few cache results, query Firestore for more
        if (results.length < 5) {
            const firestoreResults = await this.searchFirestore(searchQuery);

            // Merge results, avoiding duplicates
            const existingIds = new Set(results.map(c => c.id));
            for (const customer of firestoreResults) {
                if (!existingIds.has(customer.id)) {
                    results.push(customer);
                    this.addToCache(customer);
                }
                if (results.length >= 8) break;
            }
        }

        return results.slice(0, 8);
    }

    async searchFirestore(searchQuery) {
        const results = [];
        const collectionRef = collection(db, this.collection);
        const normalizedQuery = searchQuery.replace(/\D/g, '');
        const isPhoneSearch = normalizedQuery.length >= 3 &&
            normalizedQuery.length / searchQuery.replace(/\s/g, '').length > 0.7;

        try {
            let q;
            if (isPhoneSearch) {
                // Phone prefix search
                const endPhone = normalizedQuery + '\uf8ff';
                q = query(
                    collectionRef,
                    where('phoneDigits', '>=', normalizedQuery),
                    where('phoneDigits', '<=', endPhone),
                    limit(10)
                );
            } else {
                // Name prefix search
                const searchLower = searchQuery.toLowerCase();
                const endName = searchLower + '\uf8ff';
                q = query(
                    collectionRef,
                    where('searchName', '>=', searchLower),
                    where('searchName', '<=', endName),
                    limit(10)
                );
            }

            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
                results.push(Customer.fromFirebase(doc));
            });
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error searching Firestore:', error);
        }

        return results;
    }

    async findByPhone(phoneNumber) {
        const phoneDigits = phoneNumber.replace(/\D/g, '');

        // Check cache first
        if (this.cache.has(phoneDigits)) {
            return this.cache.get(phoneDigits);
        }

        // Query Firestore
        try {
            const collectionRef = collection(db, this.collection);
            const q = query(
                collectionRef,
                where('phoneDigits', '==', phoneDigits),
                limit(1)
            );

            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const customer = Customer.fromFirebase(querySnapshot.docs[0]);
                this.addToCache(customer);
                return customer;
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error finding customer by phone:', error);
        }

        return null;
    }

    async findOrCreate(phoneNumber, { firstName, lastName }) {
        let customer = await this.findByPhone(phoneNumber);

        if (customer) {
            // Update name if changed
            if (customer.firstName !== firstName || customer.lastName !== lastName) {
                const { searchName } = Customer.createSearchFields(phoneNumber, firstName, lastName);
                await this.updateCustomer(customer.id, {
                    firstName,
                    lastName,
                    searchName
                });
                customer.firstName = firstName;
                customer.lastName = lastName;
                customer.searchName = searchName;
            }
            return customer;
        }

        // Create new customer
        const { phoneDigits, searchName } = Customer.createSearchFields(phoneNumber, firstName, lastName);
        const storeId = await userService.getUserStoreId();
        const now = new Date().toISOString();

        const newCustomerData = {
            phoneNumber,
            phoneDigits,
            firstName,
            lastName,
            searchName,
            store_id: storeId,
            createdAt: now,
            lastOrderAt: now,
            totalOrders: 0,
            notes: ''
        };

        try {
            const collectionRef = collection(db, this.collection);
            const docRef = await addDoc(collectionRef, newCustomerData);

            customer = new Customer({
                id: docRef.id,
                ...newCustomerData
            });

            this.addToCache(customer);
            console.log('Created new customer:', customer.id);
            return customer;
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating customer:', error);
            throw error;
        }
    }

    async updateCustomer(customerId, updates) {
        try {
            const docRef = doc(db, this.collection, customerId);
            await updateDoc(docRef, updates);

            // Update cache if present
            for (const customer of this.cache.values()) {
                if (customer.id === customerId) {
                    Object.assign(customer, updates);
                    break;
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error updating customer:', error);
            throw error;
        }
    }

    async recordOrder(customerId) {
        try {
            const docRef = doc(db, this.collection, customerId);
            await updateDoc(docRef, {
                lastOrderAt: new Date().toISOString(),
                totalOrders: increment(1)
            });

            // Update cache
            for (const customer of this.cache.values()) {
                if (customer.id === customerId) {
                    customer.lastOrderAt = new Date().toISOString();
                    customer.totalOrders = (customer.totalOrders || 0) + 1;
                    break;
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error recording order for customer:', error);
        }
    }

    async getOrderHistory(customerId) {
        const orders = {
            cakes: [],
            breads: [],
            roscas: []
        };

        try {
            const storeId = await userService.getUserStoreId();

            // Get customer to find phone number
            const customerRef = doc(db, this.collection, customerId);
            const customerSnap = await getDoc(customerRef);

            if (!customerSnap.exists()) {
                console.warn('Customer not found:', customerId);
                return orders;
            }

            const phoneNumber = customerSnap.data().phoneNumber;

            // Query each order collection by phone number, filtered by store
            const collections = ['cakes', 'breads', 'roscas'];

            for (const collName of collections) {
                const collRef = collection(db, collName);
                const q = query(
                    collRef,
                    where('store_id', '==', storeId),
                    where('phoneNumber', '==', phoneNumber),
                    orderBy('date', 'desc'),
                    limit(20)
                );

                const snapshot = await getDocs(q);
                orders[collName] = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching order history:', error);
        }

        return orders;
    }

    async getOrderHistoryByPhone(phoneNumber) {
        const orders = {
            cakes: [],
            breads: [],
            roscas: []
        };

        try {
            const storeId = await userService.getUserStoreId();
            const collections = ['cakes', 'breads', 'roscas'];

            for (const collName of collections) {
                const collRef = collection(db, collName);
                const q = query(
                    collRef,
                    where('store_id', '==', storeId),
                    where('phoneNumber', '==', phoneNumber),
                    orderBy('date', 'desc'),
                    limit(20)
                );

                const snapshot = await getDocs(q);
                orders[collName] = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching order history by phone:', error);
        }

        return orders;
    }

    clearCache() {
        this.cache.clear();
        this.cacheLoaded = false;
        this.cacheTimestamp = null;
    }
}

const customerService = new CustomerService();
export default customerService;
